import React, { useEffect, useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import IconButton from "@material-ui/core/IconButton";
import DoneIcon from "@material-ui/icons/Done";
import DateRangeIcon from "@material-ui/icons/DateRange";
import CreateIcon from "@material-ui/icons/Create";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";

import DateRange, { DateRangeType } from "interfaces/DateRange";
import DropDownDialog from "components/DropDownDialog";

import useStyles from "./styles";

interface Props {
  selectedDateRange?: DateRange;
  onChange?: (dateRange: DateRange) => void;
  defaultSelectedDateRange?: DateRangeType;
  dateOptions?: DateRangeType[];
  maxDate?: Date;
  minDate?: Date;
}

const DATE_FORMAT = "eee, MMM d";

const defaultOptions = [
  DateRangeType.AllDates,
  DateRangeType.Today,
  DateRangeType.Tomorrow,
  DateRangeType.Next3Days,
  DateRangeType.Next2Days,
  DateRangeType.Custom
];

const DateRangeSelect = React.memo((props: Props) => {
  const {
    onChange,
    defaultSelectedDateRange = DateRangeType.Next2Days,
    dateOptions = defaultOptions,
    maxDate,
    minDate
  } = props;

  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [selectedDateRangeType, setSelectedDateRangeType] =
    useState<DateRangeType>(
      props.selectedDateRange?.Type || defaultSelectedDateRange
    );
  const [customDateRange, setCustomDateRange] = useState<Date[]>(
    props.selectedDateRange?.CustomDateRange || []
  );
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);

  const calendarInitialState = {
    startDate: customDateRange[0] || new Date(),
    endDate: customDateRange[1] || new Date(),
    key: "selection"
  };

  const [calendarState, setCalendarState] = useState([calendarInitialState]);

  const calendarDateFrom = calendarState[0].startDate || new Date();
  const calendarDateTo = calendarState[0].endDate || calendarDateFrom;

  const handleDialogOpen = () => {
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
    setCalendarOpen(false);
  };

  const handleSelect = (dateRangeType: DateRangeType) => () => {
    if (dateRangeType === DateRangeType.Custom) {
      handleSelectCalendar();
    } else {
      const dateRange: DateRange = {
        Type: dateRangeType,
        CustomDateRange: customDateRange
      };

      setSelectedDateRangeType(dateRangeType);

      if (onChange) {
        onChange(dateRange);
      }

      setOpen(false);
    }
  };

  const handleSelectCalendar = () => {
    setCalendarOpen(true);
  };

  const handleCalendarStateChanged = item => {
    setCalendarState([item.selection]);
  };

  const handleCalendarCancel = () => {
    setCalendarState([calendarInitialState]);
    setCalendarOpen(false);
  };

  const handleCalendarSave = () => {
    const calendarDateRange = [
      calendarState[0].startDate,
      calendarState[0].endDate
    ];

    const dateRange: DateRange = {
      Type: DateRangeType.Custom,
      CustomDateRange: calendarDateRange
    };

    setCustomDateRange(dateRange.CustomDateRange);
    setSelectedDateRangeType(dateRange.Type);
    setCalendarOpen(false);

    if (onChange) {
      onChange(dateRange);
    }

    setOpen(false);
  };

  const getCustomText = () =>
    customDateRange.length
      ? customDateRange.map(date => format(date, "MM/dd")).join(" - ")
      : "Custom Date Range";

  const dateRangeOptions = {
    [DateRangeType.Today]: {
      testName: "todayRange",
      dateRangeText: "Today"
    },
    [DateRangeType.Tomorrow]: {
      dateRangeText: "Tomorrow",
      testName: "tomorrowRange"
    },
    [DateRangeType.Next2Days]: {
      dateRangeText: "Next 2 Days",
      testName: "next2DaysRange"
    },
    [DateRangeType.Next3Days]: {
      dateRangeText: "Next 3 Days",
      testName: "next3DaysRange"
    },
    [DateRangeType.Custom]: {
      dateRangeText: getCustomText(),
      testName: "customRange"
    },
    [DateRangeType.AllDates]: {
      dateRangeText: "All Dates",
      testName: "allDatesRange"
    },
    [DateRangeType.Yesterday]: {
      dateRangeText: "Yesterday",
      testName: "yesterdayRange"
    },
    [DateRangeType.Last3Days]: {
      dateRangeText: "Last 3 Days",
      testName: "last3DaysRange"
    },
    [DateRangeType.UpcomingDates]: {
      dateRangeText: "All Upcoming Dates",
      testName: "upcomingDates"
    }
  };

  const renderCustomIcon = () => (
    <>
      {Boolean(customDateRange.length) && (
        <DoneIcon
          className={clsx(classes.listItemIconCheck, {
            [classes.listItemIconCheckSelected]:
              selectedDateRangeType === DateRangeType.Custom
          })}
        />
      )}
      {!customDateRange.length && (
        <DateRangeIcon className={classes.listItemIconDate} />
      )}
      <span className={classes.listItemDateText}>
        {dateRangeOptions[DateRangeType.Custom.toString()].dateRangeText}
      </span>
    </>
  );

  const renderDateOption = (dateRangeTypeOption: DateRangeType) => {
    const option = dateRangeOptions[dateRangeTypeOption.toString()];
    const isCustom = dateRangeTypeOption === DateRangeType.Custom;
    const text = option.dateRangeText;
    const shouldDisplayCreateButton =
      isCustom && Boolean(customDateRange.length);

    return (
      <ListItem
        button
        divider
        className={classes.listItem}
        onClick={handleSelect(dateRangeTypeOption)}
        key={option.testName}
      >
        <Button
          fullWidth
          className={classes.listItemButton}
          data-test-id={option.testName}
        >
          {isCustom ? (
            renderCustomIcon()
          ) : (
            <>
              <DoneIcon
                className={clsx(classes.listItemIconCheck, {
                  [classes.listItemIconCheckSelected]:
                    selectedDateRangeType === dateRangeTypeOption
                })}
              />
              <span className={classes.listItemText}>{text}</span>
            </>
          )}
        </Button>
        {shouldDisplayCreateButton && (
          <IconButton
            className={classes.listItemButton}
            onClick={handleSelectCalendar}
          >
            <CreateIcon className={classes.listItemIconEdit} />
          </IconButton>
        )}
      </ListItem>
    );
  };

  useEffect(() => {
    setSelectedDateRangeType(
      props.selectedDateRange?.Type || defaultSelectedDateRange
    );
    setCustomDateRange(props.selectedDateRange?.CustomDateRange || []);
  }, [props.selectedDateRange]);

  return (
    <>
      <DropDownDialog
        open={open}
        dialogWidth={calendarOpen ? 840 : 240}
        openButtonText={
          dateRangeOptions[selectedDateRangeType.toString()].dateRangeText
        }
        dialogClassName={clsx({
          [classes.dialogCalendarOpen]: calendarOpen
        })}
        dialogContentClassName={clsx({
          [classes.dialogContentCalendarOpen]: calendarOpen
        })}
        openButtonClassName={classes.dateRangeButton}
        onOpen={handleDialogOpen}
        onClose={handleDialogClose}
      >
        {!calendarOpen && (
          <List disablePadding>
            {dateOptions.map(option => renderDateOption(option))}
          </List>
        )}
        {calendarOpen && (
          <div className={classes.calendarContainer}>
            <div className={classes.selectedDates}>
              <div className={classes.selectedDatesTitle}>Select range</div>
              <div className={classes.selectedDatesContent}>
                <div className={classes.selectedDatesItem}>
                  <div className={classes.selectedDatesItemTitle}>from</div>
                  <div className={classes.selectedDatesItemValue}>
                    {format(calendarDateFrom, DATE_FORMAT)}
                  </div>
                </div>
                <div className={classes.selectedDatesItem}>
                  <div className={classes.selectedDatesItemTitle}>to</div>
                  <div className={classes.selectedDatesItemValue}>
                    {format(calendarDateTo, DATE_FORMAT)}
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.calendar}>
              <div className={classes.datePickers}>
                <DateRangePicker
                  months={2}
                  ranges={calendarState}
                  rangeColors={["#62ecb2", "#3ecf8e", "#fed14c"]}
                  direction="horizontal"
                  showSelectionPreview={true}
                  showMonthAndYearPickers={false}
                  showDateDisplay={false}
                  onChange={handleCalendarStateChanged}
                  maxDate={maxDate}
                  minDate={minDate}
                />
              </div>
            </div>

            <div className={classes.calendarButtons}>
              <Button onClick={handleCalendarCancel}>Cancel</Button>
              <Button
                className={classes.calendarSaveButton}
                onClick={handleCalendarSave}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </DropDownDialog>
    </>
  );
});

export default DateRangeSelect;
